import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { Toaster } from "sonner";
import "@/App.css";

import { ThemeProvider } from "@/context/ThemeProvider";
import { AuthProvider } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import Layout from "@/components/Layout";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Dashboard from "@/pages/Dashboard";
import NewProject from "@/pages/NewProject";
import WizardProfiles from "@/pages/WizardProfiles";
import WizardConfirm from "@/pages/WizardConfirm";
import WizardResults from "@/pages/WizardResults";
import WizardLegal from "@/pages/WizardLegal";
import WizardJudgment from "@/pages/WizardJudgment";
import WizardReport from "@/pages/WizardReport";
import Simulator from "@/pages/Simulator";
import CompareProjects from "@/pages/CompareProjects";
import PublicShare from "@/pages/PublicShare";
import MyAnalyses from "@/pages/MyAnalyses";
import Reports from "@/pages/Reports";
import Settings from "@/pages/Settings";
import About from "@/pages/About";
import DebtVita from "@/pages/DebtVita";
import DataCollector from "@/pages/DataCollector";
import AdminLayout from "@/pages/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminResources from "@/pages/admin/AdminResources";
import AdminAnalytics from "@/pages/admin/AdminAnalytics";
import AdminContacts from "@/pages/admin/AdminContacts";
import AdminContent from "@/pages/admin/AdminContent";
import AdminConfig from "@/pages/admin/AdminConfig";
import AdminSettings from "@/pages/admin/AdminSettings";
import { ForgotPassword, ResetPassword } from "@/pages/PasswordRecoveryPages";
import ContactPage from "@/pages/ContactPage";
import { FeatureFlagsProvider } from "@/context/FeatureFlagsContext";
import {
  SecurityAudit,
  SecurityEvents,
  SecuritySessions,
  SecurityIpRules,
} from "@/pages/admin/SecurityPages";

import TutorialLayout from "@/tutorial/TutorialLayout";
import TutorialHome from "@/tutorial/TutorialHome";
import QuickStart from "@/tutorial/QuickStart";
import Chapter0 from "@/tutorial/chapters/Chapter0";
import Chapter1 from "@/tutorial/chapters/Chapter1";
import Chapter2 from "@/tutorial/chapters/Chapter2";
import Chapter3 from "@/tutorial/chapters/Chapter3";
import Chapter4 from "@/tutorial/chapters/Chapter4";
import Chapter5 from "@/tutorial/chapters/Chapter5";
import Chapter6 from "@/tutorial/chapters/Chapter6";
import Chapter7 from "@/tutorial/chapters/Chapter7";
import Chapter8 from "@/tutorial/chapters/Chapter8";
import Chapter9 from "@/tutorial/chapters/Chapter9";
import Chapter10 from "@/tutorial/chapters/Chapter10";
import Chapter11 from "@/tutorial/chapters/Chapter11";

const TUTORIAL_CHAPTER_COMPONENTS = {
  0: Chapter0,
  1: Chapter1,
  2: Chapter2,
  3: Chapter3,
  4: Chapter4,
  5: Chapter5,
  6: Chapter6,
  7: Chapter7,
  8: Chapter8,
  9: Chapter9,
  10: Chapter10,
  11: Chapter11,
};

function TutorialChapterRoute() {
  const { id } = useParams();
  const Comp = TUTORIAL_CHAPTER_COMPONENTS[String(id)];
  if (!Comp) return <Navigate to="/tutorial" replace />;
  return <Comp />;
}

function AuthedShell({ children }) {
  return (
    <ProtectedRoute>
      <Layout>{children}</Layout>
    </ProtectedRoute>
  );
}

function App() {
  return (
    <ThemeProvider defaultTheme="light">
      <AuthProvider>
        <FeatureFlagsProvider>
          <BrowserRouter>
          <Toaster
            position="top-right"
            richColors
            closeButton
            toastOptions={{ duration: 4000 }}
          />
          <div className="App">
            <Routes>
              {/* Public */}
              <Route path="/" element={<Landing />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/share/:token" element={<PublicShare />} />
              <Route path="/about" element={<About />} />
              <Route path="/contact" element={<ContactPage />} />

              {/* Authed */}
              <Route
                path="/app/dashboard"
                element={<AuthedShell><Dashboard /></AuthedShell>}
              />
              <Route
                path="/app/new-project"
                element={<AuthedShell><NewProject /></AuthedShell>}
              />
              <Route
                path="/app/projects/:projectId/wizard/profiles"
                element={<AuthedShell><WizardProfiles /></AuthedShell>}
              />
              <Route
                path="/app/projects/:projectId/wizard/confirm"
                element={<AuthedShell><WizardConfirm /></AuthedShell>}
              />
              <Route
                path="/app/projects/:projectId/wizard/results"
                element={<AuthedShell><WizardResults /></AuthedShell>}
              />
              <Route
                path="/app/projects/:projectId/wizard/legal"
                element={<AuthedShell><WizardLegal /></AuthedShell>}
              />
              <Route
                path="/app/projects/:projectId/wizard/judgment"
                element={<AuthedShell><WizardJudgment /></AuthedShell>}
              />
              <Route
                path="/app/projects/:projectId/wizard/report"
                element={<AuthedShell><WizardReport /></AuthedShell>}
              />
              <Route
                path="/app/projects/:projectId/debt"
                element={<AuthedShell><DebtVita /></AuthedShell>}
              />
              <Route
                path="/app/projects/:projectId/simulator"
                element={<AuthedShell><Simulator /></AuthedShell>}
              />
              <Route
                path="/app/compare"
                element={<AuthedShell><CompareProjects /></AuthedShell>}
              />
              <Route
                path="/app/analyses"
                element={<AuthedShell><MyAnalyses /></AuthedShell>}
              />
              <Route
                path="/app/reports"
                element={<AuthedShell><Reports /></AuthedShell>}
              />
              <Route
                path="/app/settings"
                element={<AuthedShell><Settings /></AuthedShell>}
              />
              <Route
                path="/app/data-collector"
                element={<AuthedShell><DataCollector /></AuthedShell>}
              />

              {/* Help & Tutorial Center (HTC) — protected routes */}
              <Route
                path="/tutorial"
                element={
                  <ProtectedRoute>
                    <TutorialLayout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<TutorialHome />} />
                <Route path="quickstart" element={<QuickStart />} />
                <Route path="chapter/:id" element={<TutorialChapterRoute />} />
              </Route>

              {/* Redirects */}
              <Route path="/app" element={<Navigate to="/app/dashboard" replace />} />

              {/* ADMIN-VITAE™ — Phase A-1 (Socle) */}
              <Route
                path="/admin"
                element={
                  <ProtectedRoute>
                    <AdminLayout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<AdminDashboard />} />
                <Route path="security/audit" element={<SecurityAudit />} />
                <Route path="security/events" element={<SecurityEvents />} />
                <Route path="security/sessions" element={<SecuritySessions />} />
                <Route path="security/ip-rules" element={<SecurityIpRules />} />
                <Route path="settings" element={<AdminSettings />} />
                <Route path="users" element={<AdminUsers />} />
                <Route path="resources" element={<AdminResources />} />
                <Route path="analytics" element={<AdminAnalytics />} />
                <Route path="contacts" element={<AdminContacts />} />
                <Route path="content" element={<AdminContent />} />
                <Route path="config" element={<AdminConfig />} />
              </Route>

              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </div>
          </BrowserRouter>
        </FeatureFlagsProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
